import { Alert, Box, Card, Text } from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { NavigateFunction } from 'react-router'

import { useDebouncedCallback } from '@mangovoice/core'
import type { ToastLike } from '@mangovoice/mango-ui'
import { Field, LoadingState } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type {
  ApiError,
  ExtensionInstance,
  PaginatedApiResult,
} from '@mangovoice/types'

import type { SwitchboardApi } from '../api/switchboardApi'
import {
  MatchedSwitchboard,
  SwitchboardEmptyState,
  SwitchboardGrid,
  SwitchboardListHeader,
  SwitchboardListToolbar,
  useSwitchboardPagination,
} from '../components/list'
import { useListSwitchboards } from '../queries/hooks'
import type { SwitchboardTranslateFn } from '../types'
import { text } from '../utils/text'
import { SwitchboardCreatePage } from './SwitchboardCreatePage'

const EXTENSIONS_FETCH_LIMIT = 10000

export interface SwitchboardListPageProps {
  /** Switchboard API instance */
  api: SwitchboardApi
  /** Navigation function (from react-router useNavigate) */
  navigate: NavigateFunction
  /** Route template for edit page (e.g., "/switchboards/:extensionDialPlan") */
  editRouteTemplate: string
  /** PBX ID that switchboards belong to */
  pbxId: number
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Optional Field component (defaults to Chakra UI Field) */
  FieldComponent?: typeof Field
  /** Function to fetch extensions (for pagination and create modal) */
  fetchExtensions: (params: {
    pbx_id: number
    type: 'ExtensionDialPlan'
    limit?: number
    offset?: number
  }) => Promise<{
    ok: boolean
    data?: PaginatedApiResult<ExtensionInstance>
    status?: number
  }>
  /** Optional function to generate edit route from extensionDialPlan */
  getEditRoute?: (extensionDialPlan: number) => string
  /** Optional title override */
  title?: string
  /** Optional subtitle override */
  subtitle?: string
  /** Route path for the switchboards list page (for create modal navigation) */
  listRoute?: string
  /** Whether user can delete switchboards */
  canDelete?: boolean
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: SwitchboardTranslateFn
}

/**
 * Switchboard list page component
 * Displays a paginated, searchable grid of switchboard cards with create/edit/delete functionality
 */
export const SwitchboardListPage: React.FC<SwitchboardListPageProps> = ({
  api,
  navigate,
  editRouteTemplate,
  pbxId,
  toast,
  FieldComponent = Field,
  fetchExtensions,
  getEditRoute,
  title,
  subtitle,
  listRoute,
  t,
}) => {
  const queryClient = useQueryClient()

  // State
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const trimmedSearchTerm = debouncedSearchTerm.trim()
  const isServerSearchActive = trimmedSearchTerm.length > 0

  const [knownTotal, setKnownTotal] = useState(0)
  const { pageSize, setPageSize, currentPage, setCurrentPage, offset, limit } =
    useSwitchboardPagination(knownTotal)

  const applySearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchTerm(value)
    setCurrentPage(1)
  }, 300)

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value)
      applySearch(value)
    },
    [applySearch],
  )

  // Fetch switchboards
  const {
    data: switchboards,
    isLoading: isLoadingSwitchboards,
    error: switchboardsError,
  } = useListSwitchboards({
    api,
    pbxId: pbxId ?? 0,
    options: {
      enabled: !!pbxId && pbxId > 0 && !isServerSearchActive,
    },
  })

  // Fetch switchboards for server-side search mode
  const {
    data: searchedSwitchboards,
    isLoading: isLoadingSearchedSwitchboards,
    error: searchedSwitchboardsError,
  } = useListSwitchboards({
    api,
    pbxId: pbxId ?? 0,
    queryParams: {
      search: trimmedSearchTerm,
      limit,
      ...(pageSize !== 'All' && { offset }),
    },
    options: {
      enabled: !!pbxId && pbxId > 0 && isServerSearchActive,
    },
  })

  // Fetch paginated dial plan extensions for the selected PBX
  const {
    data: dialPlanExtensions,
    isLoading: isLoadingExtensions,
    error: extensionsError,
  } = useQuery<PaginatedApiResult<ExtensionInstance> | null, ApiError>({
    queryKey: ['extensions', pbxId, 'ExtensionDialPlan', pageSize, offset],
    queryFn: async () => {
      if (!pbxId) {
        return null
      }

      const response = await fetchExtensions({
        pbx_id: pbxId,
        type: 'ExtensionDialPlan',
        limit,
        ...(pageSize !== 'All' && { offset }),
      })

      if (response.ok && response.data) {
        return response.data as PaginatedApiResult<ExtensionInstance>
      }

      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      } as PaginatedApiResult<ExtensionInstance>
    },
    enabled: !!pbxId && pbxId > 0 && !isServerSearchActive,
    retry: false,
    refetchOnMount: true,
    staleTime: 0,
    gcTime: 0,
  })

  // Match extensions with switchboard data
  const matchedSwitchboards: MatchedSwitchboard[] = useMemo(() => {
    if (!dialPlanExtensions?.results || dialPlanExtensions.results.length === 0)
      return []

    type SwitchboardType = {
      extension_dial_plan: number
      updated_at: string
      json_data?: unknown
    }
    const switchboardMap = new Map<number, SwitchboardType>()
    if (switchboards?.results) {
      switchboards.results.forEach((sb) => {
        const extensionDialPlanId =
          typeof sb.extension_dial_plan === 'string'
            ? parseInt(sb.extension_dial_plan, 10)
            : sb.extension_dial_plan
        switchboardMap.set(extensionDialPlanId, sb)
      })
    }

    return dialPlanExtensions.results.map((extension) => {
      const switchboard = switchboardMap.get(extension.id)
      return { extension, switchboard }
    })
  }, [dialPlanExtensions, switchboards])

  // Match server-side searched switchboards to extension data for card rendering
  const serverSearchedSwitchboards: MatchedSwitchboard[] = useMemo(() => {
    if (!searchedSwitchboards?.results?.length) {
      return []
    }

    return searchedSwitchboards.results.map((switchboard) => {
      const extensionDialPlanId =
        typeof switchboard.extension_dial_plan === 'string'
          ? Number.parseInt(switchboard.extension_dial_plan, 10)
          : switchboard.extension_dial_plan

      // Search results don't include full ExtensionInstance fields (uuid/pbx preview),
      // but list card rendering only needs id/name/number/type/timestamps.
      const extension: ExtensionInstance & { omit_from_directory?: boolean } = {
        id: extensionDialPlanId,
        uuid: '',
        type: ExtensionType.DIAL_PLAN,
        name: switchboard.name || '',
        number: switchboard.number || String(extensionDialPlanId),
        pbx: {
          id: pbxId ?? 0,
          uuid: '',
          name: '',
        },
        voicemail_enabled: null,
        created_at: switchboard.created_at,
        updated_at: switchboard.updated_at,
      }

      if (switchboard.omit_from_directory !== undefined) {
        extension.omit_from_directory = switchboard.omit_from_directory
      }

      return {
        extension,
        switchboard: {
          extension_dial_plan: extensionDialPlanId,
          updated_at: switchboard.updated_at,
          json_data: switchboard.json_data,
        },
      }
    })
  }, [pbxId, searchedSwitchboards])

  const fetchedTotal = isServerSearchActive
    ? searchedSwitchboards?.count
    : dialPlanExtensions?.count
  const totalCount = fetchedTotal ?? 0

  useEffect(() => {
    if (typeof fetchedTotal === 'number') {
      setKnownTotal(fetchedTotal)
    }
  }, [fetchedTotal])

  const displayedSwitchboards = isServerSearchActive
    ? serverSearchedSwitchboards
    : matchedSwitchboards

  const getNavigationHref = useCallback(
    (extensionDialPlan: number) => {
      if (getEditRoute) {
        return getEditRoute(extensionDialPlan)
      }

      return editRouteTemplate.replace(
        ':extensionDialPlan',
        String(extensionDialPlan),
      )
    },
    [editRouteTemplate, getEditRoute],
  )

  const handleFetchExtensions = useCallback(
    async (params: {
      pbx_id: number
      type: 'ExtensionDialPlan'
      limit?: number
    }) => {
      const response = await fetchExtensions({
        pbx_id: params.pbx_id,
        type: params.type,
        limit: params.limit || EXTENSIONS_FETCH_LIMIT,
      })
      return {
        ok: response.ok,
        data: response.data,
        status: (response as { status?: number }).status,
      }
    },
    [fetchExtensions],
  )

  const handleMetadataUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['switchboards'] })
    queryClient.invalidateQueries({ queryKey: ['extensions'] })
  }, [queryClient])

  const isLoading = isServerSearchActive
    ? isLoadingSearchedSwitchboards
    : isLoadingSwitchboards || isLoadingExtensions
  const error = isServerSearchActive
    ? searchedSwitchboardsError
    : switchboardsError || extensionsError

  const hasNoData = matchedSwitchboards.length === 0 && !trimmedSearchTerm
  const hasNoSearchResults =
    displayedSwitchboards.length === 0 && isServerSearchActive
  const hasNoResults = displayedSwitchboards.length === 0

  const getContentState = ():
    | 'error'
    | 'loading'
    | 'no-data'
    | 'no-search-results'
    | 'no-results'
    | 'data' => {
    if (error) return 'error'
    if (isLoading) return 'loading'
    if (hasNoData) return 'no-data'
    if (hasNoSearchResults) return 'no-search-results'
    if (hasNoResults) return 'no-results'
    return 'data'
  }

  const contentState = getContentState()

  const contentMap = {
    error: (
      <Alert.Root status="error" p={4}>
        <Alert.Indicator />
        <Box>
          <Text fontWeight="bold">
            {text(t, 'switchboard.list.grid.errorTitle', 'Error')}
          </Text>
          <Text>
            {text(
              t,
              'switchboard.list.grid.errorLoading',
              'Error loading switchboards:',
            )}{' '}
            {error?.message || JSON.stringify(error)}
          </Text>
          <Text fontSize="sm" mt={2} color="fg.muted">
            {text(
              t,
              'switchboard.list.grid.checkConsole',
              'Check console for details',
            )}
          </Text>
        </Box>
      </Alert.Root>
    ),
    loading: (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="bg.subtle"
        borderRadius="lg"
      >
        <LoadingState
          title={text(
            t,
            'switchboard.list.grid.loading',
            'Loading switchboards...',
          )}
        />
      </Box>
    ),
    'no-data': <SwitchboardEmptyState type="no-data" t={t} />,
    'no-search-results': (
      <SwitchboardEmptyState type="no-search-results" t={t} />
    ),
    'no-results': <SwitchboardEmptyState type="no-results" t={t} />,
    data: (
      <SwitchboardGrid
        switchboards={displayedSwitchboards}
        pbxId={pbxId}
        canDelete={false}
        isDeleting={false}
        getNavigationHref={getNavigationHref}
        api={api}
        toast={toast}
        onMetadataUpdated={handleMetadataUpdated}
        t={t}
      />
    ),
  }

  // Calculate list route for create modal
  const resolvedListRoute = listRoute || editRouteTemplate.split('/:')[0] || '/'

  const containerHeight = '100%'

  return (
    <Box h={containerHeight} w="100%" display="flex" flexDirection="column">
      <Card.Root
        flex={1}
        display="flex"
        flexDirection="column"
        minH={0}
        border="none"
        overflow="hidden"
      >
        <Card.Header p={6} flexShrink={0}>
          <SwitchboardListHeader
            searchTerm={searchInput}
            onSearchChange={handleSearchChange}
            onNewClick={() => setIsCreateModalOpen(true)}
            title={title}
            subtitle={subtitle}
            t={t}
          />

          <SwitchboardListToolbar
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            currentPage={currentPage}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            isLoading={isLoading}
            showToolbar={true}
            t={t}
          />
        </Card.Header>

        <Card.Body
          px={6}
          py={3}
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
        >
          <Box
            flex={1}
            minH={0}
            overflowY="auto"
            bg={hasNoResults ? 'bg.subtle' : undefined}
            borderRadius={hasNoResults ? 'lg' : undefined}
          >
            {contentMap[contentState]}
          </Box>
        </Card.Body>
      </Card.Root>

      {/* Create Switchboard Modal */}
      <SwitchboardCreatePage
        api={api}
        navigate={navigate}
        listRoute={resolvedListRoute}
        editRouteTemplate={editRouteTemplate}
        pbxId={pbxId}
        toast={toast}
        FieldComponent={FieldComponent}
        open={isCreateModalOpen}
        onOpenChange={({ open }: { open: boolean }) =>
          setIsCreateModalOpen(open)
        }
        fetchExtensions={handleFetchExtensions}
        t={t}
      />
    </Box>
  )
}
